import {
  Component,
  signal,
  computed,
  inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { injectQuery, queryOptions } from "@ngneat/query";
import { derivedAsync } from "ngxtension/derived-async";
import { forkJoin } from "rxjs";
import { map } from "rxjs/operators";
import { InvoicesTableComponent } from "../invoices-table/invoices-table.component";
import { InvoicesHeaderComponent } from "../invoices-header/invoices-header.component";
import { HorusMessageGeneralComponent } from "@shared/components/domain/horus-message-general/horus-message-general.component";
import { HorusLoaderComponent } from "@shared/components/domain/horus-loader/horus-loader.component";
import { InvoiceService } from "../../services/invoice.service";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { Invoice } from "../../models/invoice.models";
import { formatDate } from "@core/utils/date.util";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

@Component({
  selector: "app-invoice-tab",
  standalone: true,
  imports: [
    CommonModule,
    InvoicesTableComponent,
    InvoicesHeaderComponent,
    HorusMessageGeneralComponent,
    HorusLoaderComponent,
  ],
  template: `
    <div class="mx-xl mt-md bg-white rounded-md">
      @if (invoicesQuery().isLoading) {
        <div class="loading-state">
          <app-horus-loader></app-horus-loader>
          <div class="loading-text">Cargando información de comprobantes</div>
        </div>
      } @else if (invoicesQuery().isError) {
        <div class="error-state">Error al cargar los comprobantes</div>
      } @else if (filteredInvoices().length === 0) {
        <div class="no-data-container max-w-[400px] mx-auto">
          <app-horus-message-general
            nameIcon="noDataMessageIcon"
            btnAction="LIMPIAR VALORES"
            (action)="clearFilters()"
          >
            <span style="color:#F6D44D; font-size: 14px; line-height: 25px;"
              >Todavía no hay nada por aquí</span
            ><br />
            <span style="font-weight: 400; margin-top: 5px;" class="text-[18px]"
              >Aún no tenemos información sobre
              <strong>&lt;Comprobantes de pago&gt;</strong> en relación a tu
              búsqueda. Prueba usando otros valores.</span
            >
          </app-horus-message-general>
        </div>
      } @else {
        <app-invoices-header
          #invoicesHeader
          (documentFilterChange)="onDocumentFilterChange($event)"
          (receiptTypeFilterChange)="onReceiptTypeFilterChange($event)"
        >
        </app-invoices-header>
        <div class="mx-md">
          <app-invoices-table
            [invoices]="filteredInvoices()"
            [isLoading]="invoicesQuery().isLoading"
            [isError]="invoicesQuery().isError"
          >
          </app-invoices-table>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .loading-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        font-size: 16px;
        color: #666;
      }

      .loading-state app-horus-loader {
        width: 30px;
        height: 30px;

        ::ng-deep .horus-loader {
          width: 100%;
          height: 100%;

          svg {
            width: 100%;
            height: 100%;
          }
        }
      }

      .loading-text {
        margin-top: 16px;
        font-size: 14px;
        color: #666;
      }

      .no-data-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        padding: 40px 20px;
      }

      .error-state {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        font-size: 16px;
        color: #666;
      }
    `,
  ],
})
export class InvoiceTabComponent {
  private invoiceService = inject(InvoiceService);
  private tenantUrlUtil = inject(TenantUrlUtilService);
  private query = injectQuery();

  @ViewChild("invoicesHeader") invoicesHeader!: InvoicesHeaderComponent;

  documentFilter = signal<string[]>([]);
  receiptTypeFilter = signal<string[]>([]);

  private getInvoicesQueryOptions(
    tenant: string,
    documentType: string,
    documentValue: string,
    dateRange: any,
  ) {
    return queryOptions({
      queryKey: [
        "all-invoices",
        tenant,
        documentType,
        documentValue,
        dateRange,
      ] as const,
      queryFn: () => {
        const isIdentityDocument = documentType === "IDENTITY_DOCUMENT";
        const isEmail = documentType === "EMAIL";
        const isOrderId =
          documentType === "ORDER_ID" || documentType === "SALE_NOTE_NUMBER";

        const transactionCall =
          isIdentityDocument && dateRange
            ? this.invoiceService.getTransactionInvoices(
                tenant,
                documentValue,
                dateRange.dateFrom,
                dateRange.dateTo,
              )
            : Promise.resolve({
                code: 200,
                message: "Skipped - not identity document or no date range",
                data: [],
              });

        let invoicingCall;
        if (isIdentityDocument) {
          invoicingCall = this.invoiceService.getInvoicingDocuments({
            tenantId: tenant,
            identityDocument: documentValue,
            dateRange,
          });
        } else if (isEmail) {
          invoicingCall = this.invoiceService.getInvoicingDocuments({
            tenantId: tenant,
            email: documentValue,
            dateRange,
          });
        } else if (isOrderId) {
          invoicingCall = this.invoiceService.getInvoicingDocuments({
            tenantId: tenant,
            orderId: documentValue,
            dateRange,
          });
        } else {
          invoicingCall = Promise.resolve([]);
        }

        return forkJoin({
          transaction: transactionCall,
          invoicing: invoicingCall,
        }).pipe(
          map(({ transaction, invoicing }) => {
            const allInvoices: Invoice[] = [];

            if (transaction.data) {
              const transactionMapped = transaction.data.map((item) => {
                const docInfo = this.getTypeDocument(item.tipoDocumento);

                return {
                  amount: this.formatSoles(item.monto),
                  date: item.fecha,
                  documentType: docInfo.type,
                  receiptTypeLabel: "Comprobante Físico",
                  documentTypeLabel: docInfo.label,
                  folio: item.serieFolio,
                  location: item.local,
                  locationName: item.nombreLocal,
                  documentTypeId: docInfo.id,
                  rucEmpresa: item.rucEmpresa,
                  receiptType: "fisico" as const,
                } as Invoice;
              });
              allInvoices.push(...transactionMapped);
            }

            const invoicingMapped = invoicing.map((item) => {
              const docInfo = this.getTypeDocument(item.documentType);

              return {
                amount: this.formatSoles(item.amount),
                date: formatDate(item.documentDate),
                documentType: docInfo.type,
                receiptTypeLabel: "Comprobante Virtual",
                documentTypeLabel: docInfo.label,
                folio: item.documentNumber,
                location: "----",
                documentTypeId: docInfo.id,
                invoicePdfUrl: item.documentURL,
                receiptType: "virtual" as const,
                orderId: item.orderId,
              } as Invoice;
            });

            allInvoices.push(...invoicingMapped);

            // Sort from most recent to oldest using dayjs
            const result = allInvoices.sort((a, b) => {
              const dateA = dayjs(a.date, "DD/MM/YYYY HH:mm:ss");
              const dateB = dayjs(b.date, "DD/MM/YYYY HH:mm:ss");
              return dateB.valueOf() - dateA.valueOf();
            });
            return result;
          }),
        );
      },
    });
  }

  readonly invoicesQueryAsync = derivedAsync(() => {
    const tenant = this.tenantUrlUtil.currentTenant();
    const documentType = this.tenantUrlUtil.documentType();
    const documentValue = this.tenantUrlUtil.documentValue();
    const dateRange = this.tenantUrlUtil.dateRangeForAPI();

    if (!tenant || !documentValue) {
      return null;
    }

    return this.query({
      ...this.getInvoicesQueryOptions(
        tenant,
        documentType,
        documentValue,
        dateRange,
      ),
      enabled: !!tenant && !!documentValue,
    }).result$;
  });

  invoicesQuery = computed(() => {
    const query = this.invoicesQueryAsync();
    if (!query) {
      return {
        isLoading: false,
        isError: false,
        isSuccess: false,
        data: null,
      };
    }
    return query;
  });

  allInvoices = computed(() => {
    const query = this.invoicesQueryAsync();
    return query?.data ?? [];
  });

  filteredInvoices = computed(() => {
    let invoices = this.allInvoices();

    const receiptFilter = this.receiptTypeFilter();
    if (receiptFilter.length > 0) {
      invoices = invoices.filter((invoice) =>
        receiptFilter.includes(invoice.receiptType),
      );
    }

    const docFilter = this.documentFilter();
    if (docFilter.length > 0) {
      invoices = invoices.filter((invoice) =>
        docFilter.includes(invoice.documentType),
      );
    }

    return invoices;
  });

  private formatSoles(amount: number): string {
    return new Intl.NumberFormat("es-PE", {
      style: "currency",
      currency: "PEN",
      minimumFractionDigits: 2,
    }).format(amount);
  }

  private getTypeDocument(value: string): {
    type: Invoice["documentType"];
    label: string;
    id: number;
  } {
    const mapper: Record<
      string,
      { values: string[]; label: string; id: number }
    > = {
      boleta: {
        values: ["BOLETA", "BLT", "Boleta"],
        label: "Boleta de pago",
        id: 3,
      },
      factura: {
        values: ["FACTURA", "FCT", "TFC"],
        label: "Factura",
        id: 1,
      },
      "nota-credito": {
        values: ["NCR", "NOTA DE CREDITO"],
        label: "Nota de crédito",
        id: 7,
      },
    };

    for (const [type, { values, label, id }] of Object.entries(mapper)) {
      if (values.includes(value)) {
        return { type: type as Invoice["documentType"], label, id };
      }
    }
    return {
      label: "Desconocido",
      type: "desconocido" as any,
      id: 0,
    };
  }

  onDocumentFilterChange(selectedDocuments: string[]): void {
    this.documentFilter.set(selectedDocuments);
  }

  onReceiptTypeFilterChange(selectedReceiptTypes: string[]): void {
    this.receiptTypeFilter.set(selectedReceiptTypes);
  }

  clearFilters(): void {
    this.documentFilter.set([]);
    this.receiptTypeFilter.set([]);

    if (this.invoicesHeader) {
      this.invoicesHeader.documentControl.setValue([]);
      this.invoicesHeader.receiptTypeControl.setValue([]);
    }
  }
}
